import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import StyledDropdown from './StyledDropdown';

const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage = 10,
  onPageChange,
  showItemsPerPage = false,
  onItemsPerPageChange,
  itemsPerPageOptions = [10, 25, 50, 100],
}) => {
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  // Don't render if there's only one page or no items
  if (totalPages <= 1 && !showItemsPerPage) {
    return null;
  }

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 transition-colors duration-200">
      {/* Items info */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-gray-600 dark:text-dark-muted">
          Showing <span className="font-medium">{startItem}</span> to{' '}
          <span className="font-medium">{endItem}</span> of{' '}
          <span className="font-medium">{totalItems}</span> results
        </span>
        
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-dark-muted">Per page:</span>
            <StyledDropdown
              options={itemsPerPageOptions.map((n) => ({ value: n, label: String(n) }))}
              value={itemsPerPage}
              onChange={(val) => onItemsPerPageChange(Number(val))}
              placeholder="Per page"
              className="w-20"
            />
          </div>
        )}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          {/* First page */}
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className={`p-1.5 neo-icon-btn transition-all ${
              currentPage === 1
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                : 'text-gray-600 dark:text-dark-muted'
            }`}
            title="First page"
          >
            <ChevronsLeft size={18} />
          </button>

          {/* Previous page */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-1.5 neo-icon-btn transition-all ${
              currentPage === 1
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                : 'text-gray-600 dark:text-dark-muted'
            }`}
            title="Previous page"
          >
            <ChevronLeft size={18} />
          </button>

          {/* Page numbers */}
          <div className="flex items-center gap-1 mx-2">
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-gray-400 dark:text-dark-muted">...</span>
                ) : (
                  <button
                    onClick={() => onPageChange(page)}
                    className={`min-w-[32px] px-2 py-1 text-sm font-medium transition-all ${
                      currentPage === page
                        ? 'neo-btn-primary'
                        : 'neo-btn text-gray-600 dark:text-dark-muted'
                    }`}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Next page */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`p-1.5 neo-icon-btn transition-all ${
              currentPage === totalPages
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                : 'text-gray-600 dark:text-dark-muted'
            }`}
            title="Next page"
          >
            <ChevronRight size={18} />
          </button>

          {/* Last page */}
          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className={`p-1.5 neo-icon-btn transition-all ${
              currentPage === totalPages
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed opacity-50'
                : 'text-gray-600 dark:text-dark-muted'
            }`}
            title="Last page"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

// Custom hook for pagination logic
export const usePagination = (items, initialItemsPerPage = 10) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(initialItemsPerPage);

  // Reset to page 1 when items change significantly
  React.useEffect(() => {
    const totalPages = Math.ceil(items.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [items.length, itemsPerPage, currentPage]);

  const paginatedItems = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return items.slice(startIndex, startIndex + itemsPerPage);
  }, [items, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const resetPage = React.useCallback(() => {
    setCurrentPage(1);
  }, []);

  return {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalItems: items.length,
    handlePageChange,
    handleItemsPerPageChange,
    resetPage,
  };
};

export default Pagination;








